import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    useDroppable,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Users, Tag, Megaphone, ArrowRight } from 'lucide-react';

/* 
 * Sortable Item for Offers
 */
function SortableOfferItem({ id, offer }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="bg-slate-800 p-3 rounded-xl border border-slate-700 flex flex-col gap-2 cursor-grab active:cursor-grabbing hover:border-primary/50 transition-colors">
            <div className="flex justify-between items-start">
                <div>
                    <div className="font-bold text-slate-200 text-sm">{offer.name}</div>
                    <div className="text-[10px] font-mono text-slate-500">ID: {offer.id_tarifa}</div>
                </div>
                <div className="text-lg font-black text-white">{Number(offer.precio).toFixed(2)}€</div>
            </div>
            {offer.slogan && (
                <div className="text-[10px] font-medium text-amber-400 bg-amber-500/10 px-2 py-1 rounded inline-block self-start">
                    {offer.slogan}
                </div>
            )}
        </div>
    );
}

/* 
 * Sortable Item for Users
 */
function SortableUserItem({ id, user }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="bg-slate-800 p-3 rounded-xl border border-slate-700 flex flex-col cursor-grab active:cursor-grabbing hover:border-primary/50 transition-colors">
            <div className="font-bold text-slate-200 text-sm">{user.full_name || 'Sin Nombre'}</div>
            <div className="text-xs text-slate-400 truncate">{user.email}</div>
        </div>
    );
}

/*
 * Droppable Container holding Sortable Context
 */
function DroppableContainer({ id, items, title, emptyText, type = 'offer' }) {
    const { setNodeRef } = useDroppable({ id });

    return (
        <div ref={setNodeRef} className="flex flex-col bg-slate-900/50 rounded-2xl border border-slate-800 h-[600px] overflow-hidden">
            <div className="p-4 border-b border-slate-800 bg-slate-900 flex justify-between items-center">
                <h3 className="font-bold text-slate-300">{title}</h3>
                <span className="bg-slate-800 text-slate-400 text-xs px-2 py-0.5 rounded-full font-mono">{items.length}</span>
            </div>
            <div className="p-4 flex-1 overflow-y-auto space-y-3 custom-scrollbar">
                <SortableContext id={id} items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                    {items.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-slate-500 text-sm italic py-10">{emptyText}</div>
                    ) : (
                        items.map((item) => (
                            type === 'offer'
                                ? <SortableOfferItem key={item.id} id={item.id} offer={item} />
                                : <SortableUserItem key={item.id} id={item.id} user={item} />
                        ))
                    )}
                </SortableContext>
            </div>
        </div>
    );
}

const AssignmentDragDrop = () => {
    const { authFetch } = useAuth();
    const [mode, setMode] = useState('offers'); // 'offers' or 'users'

    // Data sources
    const [campaigns, setCampaigns] = useState([]);
    const [allOffers, setAllOffers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);

    // UI State
    const [selectedCampaignId, setSelectedCampaignId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeDragItem, setActiveDragItem] = useState(null); // For overlay

    // Dnd State for Offers (N:M)
    // Left: all active offers not currently in the campaign. Right: offers in the campaign.
    const [availableOffers, setAvailableOffers] = useState([]);
    const [assignedOffers, setAssignedOffers] = useState([]);

    // Dnd State for Users (1:N)
    // Left: all users NOT in this campaign. Right: users IN this campaign.
    const [availableUsers, setAvailableUsers] = useState([]);
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [activeContainerAtStart, setActiveContainerAtStart] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );



    const fetchAllData = useCallback(async () => {
        setLoading(true);
        try {
            const [cRes, oRes, uRes] = await Promise.all([
                authFetch('/api/backoffice/campaigns'),
                authFetch('/api/backoffice/offers'),
                authFetch('/api/backoffice/users')
            ]);

            const [cData, oData, uData] = await Promise.all([
                cRes.ok ? cRes.json() : [],
                oRes.ok ? oRes.json() : [],
                uRes.ok ? uRes.json() : []
            ]);

            setCampaigns(cData.filter(c => c.active)); // Only active campaigns for mapping
            setAllOffers(oData.filter(o => o.active)); // Only active offers
            setAllUsers(uData);

            if (cData.length > 0) {
                setSelectedCampaignId(cData[0].id);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [authFetch]);

    useEffect(() => {
        fetchAllData();
    }, [fetchAllData]);

    const loadCampaignOffers = useCallback(async (campId) => {
        try {
            const res = await authFetch(`/api/backoffice/campaigns/${campId}/offers`);
            if (res.ok) {
                const data = await res.json();
                const assignedIds = data.offer_ids || [];

                const assigned = allOffers.filter(o => assignedIds.includes(o.id));
                const available = allOffers.filter(o => !assignedIds.includes(o.id));

                setAssignedOffers(assigned);
                setAvailableOffers(available);
            }
        } catch (err) {
            console.error(err);
        }
    }, [allOffers, authFetch]);

    // Whenever selected campaign or mode changes, recompute the buckets
    useEffect(() => {
        if (!selectedCampaignId) return;

        if (mode === 'offers') {
            loadCampaignOffers(selectedCampaignId);
        } else {
            // For users, it's strictly client-side filtering based on allUsers campaign_id
            const assigned = allUsers.filter(u => u.campaign_id === selectedCampaignId);
            const available = allUsers.filter(u => u.campaign_id !== selectedCampaignId);
            setAssignedUsers(assigned);
            setAvailableUsers(available);
        }
    }, [selectedCampaignId, mode, allUsers, loadCampaignOffers]);

    const handleDragStart = (event) => {
        const { active } = event;
        const id = active.id;

        // Find container by checking current state if sortable info is missing
        const containerId = active.data.current?.sortable?.containerId ||
            (availableOffers.find(o => o.id === id) ? 'available' :
                assignedOffers.find(o => o.id === id) ? 'assigned' :
                    availableUsers.find(u => u.id === id) ? 'available' :
                        assignedUsers.find(u => u.id === id) ? 'assigned' : null);

        setActiveContainerAtStart(containerId);

        // Find the item to display in the overlay
        if (mode === 'offers') {
            const target = [...availableOffers, ...assignedOffers].find(o => o.id === id);
            setActiveDragItem({ type: 'offer', data: target });
        } else {
            const target = [...availableUsers, ...assignedUsers].find(u => u.id === id);
            setActiveDragItem({ type: 'user', data: target });
        }
    };

    const handleDragOver = (event) => {
        const { active, over } = event;
        if (!over) return;

        const activeContainer = active.data.current?.sortable?.containerId;
        const overContainer = over.data.current?.sortable?.containerId || over.id; // could be empty container id

        if (!activeContainer || !overContainer || activeContainer === overContainer) {
            return;
        }

        // Moving between containers
        if (mode === 'offers') {
            setItemsForContainer(activeContainer, prev => prev.filter(item => item.id !== active.id));
            setItemsForContainer(overContainer, prev => {
                const item = [...availableOffers, ...assignedOffers].find(o => o.id === active.id);
                // Inserting at the destination
                const overIndex = getItemsForContainer(overContainer).findIndex(i => i.id === over.id);
                const newIndex = overIndex >= 0 ? overIndex : prev.length;
                return [...prev.slice(0, newIndex), item, ...prev.slice(newIndex)];
            });
        } else {
            // Users
            setItemsForContainer(activeContainer, prev => prev.filter(item => item.id !== active.id));
            setItemsForContainer(overContainer, prev => {
                const item = [...availableUsers, ...assignedUsers].find(u => u.id === active.id);
                const overIndex = getItemsForContainer(overContainer).findIndex(i => i.id === over.id);
                const newIndex = overIndex >= 0 ? overIndex : prev.length;
                return [...prev.slice(0, newIndex), item, ...prev.slice(newIndex)];
            });
        }
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        const startContainer = activeContainerAtStart;
        setActiveDragItem(null);
        setActiveContainerAtStart(null);

        if (!over) return;

        const overContainer = over.data.current?.sortable?.containerId || over.id;

        if (!startContainer || !overContainer) return;

        if (startContainer === overContainer) {
            // Reordering within same container
            const items = getItemsForContainer(startContainer);
            const oldIndex = items.findIndex(i => i.id === active.id);
            const newIndex = items.findIndex(i => i.id === over.id);

            if (oldIndex !== newIndex && oldIndex !== -1 && newIndex !== -1) {
                const newItems = arrayMove(items, oldIndex, newIndex);
                setItemsForContainer(startContainer, newItems);
                if (mode === 'offers' && startContainer === 'assigned') {
                    saveOffersMapping(newItems.map(o => o.id));
                }
            }
        } else {
            // Persistence for cross-container moves
            if (mode === 'offers') {
                let finalAssignedIds;
                if (overContainer === 'assigned') {
                    // Moving TO assigned: take current assigned + the new one, ensure NO duplicates
                    const currentIds = assignedOffers.map(o => o.id);
                    finalAssignedIds = Array.from(new Set([...currentIds, active.id]));
                } else {
                    // Moving TO available: remove from assigned
                    finalAssignedIds = assignedOffers.filter(o => o.id !== active.id).map(o => o.id);
                }
                saveOffersMapping(finalAssignedIds);
            } else if (mode === 'users') {
                const targetCampaignId = overContainer === 'assigned' ? selectedCampaignId : null;
                saveUserCampaign(active.id, targetCampaignId);
            }
        }
    };

    const saveOffersMapping = async (offerIds) => {
        try {
            await authFetch(`/api/backoffice/campaigns/${selectedCampaignId}/offers`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ offer_ids: offerIds })
            });
        } catch (err) {
            console.error(err);
        }
    };

    const saveUserCampaign = async (userId, campaignId) => {
        try {
            await authFetch(`/api/backoffice/users/${userId}/campaign`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ campaign_id: campaignId })
            });

            // Re-sync allUsers state so if they switch campaigns the user is properly located
            setAllUsers(prev => prev.map(u => u.id === userId ? { ...u, campaign_id: campaignId } : u));

        } catch (err) {
            console.error(err);
        }
    };

    const getItemsForContainer = (containerId) => {
        if (mode === 'offers') {
            return containerId === 'available' ? availableOffers : assignedOffers;
        } else {
            return containerId === 'available' ? availableUsers : assignedUsers;
        }
    };

    const setItemsForContainer = (containerId, setterFn) => {
        if (mode === 'offers') {
            if (containerId === 'available') setAvailableOffers(setterFn);
            if (containerId === 'assigned') setAssignedOffers(setterFn);
        } else {
            if (containerId === 'available') setAvailableUsers(setterFn);
            if (containerId === 'assigned') setAssignedUsers(setterFn);
        }
    };

    if (loading) return <div className="text-slate-500 animate-pulse">Cargando datos...</div>;

    if (campaigns.length === 0) return <div className="text-slate-500">Es necesario crear primero una campaña activa.</div>;

    return (
        <div className="space-y-6 flex flex-col h-[calc(100vh-80px)]">
            <div className="flex justify-between items-center bg-slate-900/50 p-6 rounded-2xl border border-slate-800 flex-shrink-0">
                <div>
                    <h1 className="text-2xl font-bold">Asignaciones</h1>
                    <p className="text-slate-400 text-sm">Arrastra y suelta para gestionar ofertas y usuarios por campaña.</p>
                </div>
                <div className="flex bg-slate-950 rounded-lg p-1 border border-slate-800">
                    <button
                        onClick={() => setMode('offers')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'offers' ? 'bg-primary text-primary-foreground shadow' : 'text-slate-400 hover:text-slate-200'}`}
                    >
                        <Tag size={16} /> Campañas ↔ Ofertas
                    </button>
                    <button
                        onClick={() => setMode('users')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'users' ? 'bg-primary text-primary-foreground shadow' : 'text-slate-400 hover:text-slate-200'}`}
                    >
                        <Users size={16} /> Campañas ↔ Usuarios
                    </button>
                </div>
            </div>

            <div className="flex gap-6 flex-1 min-h-0">
                {/* Fixed Campaign Selector */}
                <div className="w-64 bg-slate-900 border border-slate-800 rounded-2xl flex flex-col overflow-hidden">
                    <div className="p-4 border-b border-slate-800 bg-slate-950/50 flex items-center gap-2">
                        <Megaphone size={18} className="text-slate-400" />
                        <h3 className="font-bold">Campañas Activas</h3>
                    </div>
                    <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
                        {campaigns.map(camp => (
                            <button
                                key={camp.id}
                                onClick={() => setSelectedCampaignId(camp.id)}
                                className={`w-full text-left px-4 py-3 rounded-xl transition-all border ${selectedCampaignId === camp.id ? 'bg-primary/20 border-primary/50 text-white shadow-inner font-bold' : 'bg-slate-800/30 border-slate-800 text-slate-400 hover:bg-slate-800/80 hover:text-slate-200'}`}
                            >
                                <div className="truncate">{camp.name}</div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Dnd Area */}
                <div className="flex-1 bg-slate-950 rounded-2xl border border-slate-800 p-6 flex items-stretch gap-6">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDragEnd={handleDragEnd}
                    >
                        <div className="flex-1">
                            <DroppableContainer
                                id="available"
                                title={mode === 'offers' ? "Ofertas Disponibles" : "Otros Usuarios"}
                                items={mode === 'offers' ? availableOffers : availableUsers}
                                type={mode === 'offers' ? 'offer' : 'user'}
                                emptyText={mode === 'offers' ? "No hay ofertas por asignar." : "No hay usuarios fuera de esta campaña."}
                            />
                        </div>

                        <div className="flex items-center justify-center text-slate-700">
                            <ArrowRight size={32} />
                        </div>

                        <div className="flex-1">
                            <DroppableContainer
                                id="assigned"
                                title={mode === 'offers' ? "Asignadas a Campaña" : "Usuarios en Campaña"}
                                items={mode === 'offers' ? assignedOffers : assignedUsers}
                                type={mode === 'offers' ? 'offer' : 'user'}
                                emptyText="Arrastra elementos aquí."
                            />
                        </div>

                        <DragOverlay>
                            {activeDragItem ? (
                                activeDragItem.type === 'offer'
                                    ? <div className="opacity-80 scale-105 shadow-2xl rotate-2"><SortableOfferItem id="overlay" offer={activeDragItem.data} /></div>
                                    : <div className="opacity-80 scale-105 shadow-2xl rotate-2"><SortableUserItem id="overlay" user={activeDragItem.data} /></div>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                </div>
            </div>
        </div>
    );
};

export default AssignmentDragDrop;
